// Phase 6.B-3-redo — 매장분류 시안 layout 1:1 port.
// 기준: design/v3/SettingsPage.jsx:80-223 (StoreDirectory) + :226-266 (StoreDetail).
//
// Layout:
//   - Platform horizontal tabs (배민/쿠팡이츠/요기요/오프라인 + 동적 등록 플랫폼)
//   - Platform 탭 옆에 "+ 플랫폼 추가" 버튼 (inline form, owner only)
//   - 2-column grid (260px stores list + 1fr store detail)
//   - 좌측 stores list: 헤더 (플랫폼 · 상점 N + "+ 추가") + list + inline add-store form
//   - 우측 store detail: header (icon+brand+badge+편집/삭제) + SubNav (메뉴등록+상점정보) + tab body
//   - SubNav: 메뉴등록, 상점정보 — **레시피 제거** (사용자 명시, 별도 페이지로 이전)
//   - Selection 은 같은 페이지 안의 state. URL: /settings/stores?storeId=xxx (옵션)

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  locationsControllerCreate,
  locationsControllerList,
  locationsControllerRemove,
  locationsControllerUpdate,
  locationSalesChannelsControllerCreate,
  locationSalesChannelsControllerList,
  locationSalesChannelsControllerRemove,
  menusControllerListMaster,
  platformsControllerUpdate,
  storeMenusControllerCreate,
  storeMenusControllerList,
  storeMenusControllerRemove,
  storeMenusControllerUpdate,
  storesControllerCreate,
  storesControllerList,
  storesControllerRemove,
  storesControllerUpdate,
} from '@dvvb/sdk';

import {
  AlertBanner,
  Badge,
  Button,
  EmptyState,
  FormField,
  Icon,
  Modal,
  Select,
  StatusDot,
  SubNav,
  type SubNavItem,
  TableHeader,
  TableRow,
  TableShell,
  TextInput,
  Toggle,
  useToast,
} from '../../components/primitives';
import { unwrap, ApiCallError } from '../../lib/envelope';
import { useAuth } from '../../auth/AuthContext';
import { queryClient } from '../../lib/queryClient';
import type { LocationRow, MenuRow, StoreMenuRow, StoreRow } from '../../types/api';

function formatWon(s: string | number): string {
  const n = typeof s === 'string' ? Number(s) : s;
  return '₩' + (Number.isFinite(n) ? Math.round(n) : 0).toLocaleString('ko-KR');
}

interface PlatformDto {
  id: string;
  name: string;
  shortName: string;
  color: string;
  displayOrder: number;
  isActive: boolean;
  // Phase 6.B-7 — 분석에서 매출 - 수수료율*매출 - 배달비 - 레시피 원가 = 순이익 계산용.
  // 0.15 = 15%. deliveryFee 는 정수 (원).
  commissionRate: number;
  deliveryFee: number;
}

interface PlatformsEnvelope {
  items: PlatformDto[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}
interface StoresEnvelope {
  items: StoreRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}
interface LocationsEnvelope {
  items: LocationRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}
interface StoreMenusEnvelope {
  items: StoreMenuRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}
interface MasterMenusEnvelope {
  items: MenuRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

const OFFLINE_COLOR = 'var(--earth)';
const ORDER_SMS_SENDER_SETTINGS_KEY = 'autoOrderSmsSender';

interface OrderSmsSenderSettings {
  displayName: string;
  phone: string;
  address: string;
  businessName: string;
  businessNumber: string;
  defaultMemo: string;
}

function emptyOrderSmsSenderSettings(): OrderSmsSenderSettings {
  return {
    displayName: '',
    phone: '',
    address: '',
    businessName: '',
    businessNumber: '',
    defaultMemo: '',
  };
}

function readOrderSmsSenderSettings(
  settings: Record<string, unknown> | undefined,
): OrderSmsSenderSettings {
  const raw = settings?.[ORDER_SMS_SENDER_SETTINGS_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return emptyOrderSmsSenderSettings();
  }
  const source = raw as Record<string, unknown>;
  return {
    displayName: stringSetting(source.displayName),
    phone: stringSetting(source.phone),
    address: stringSetting(source.address),
    businessName: stringSetting(source.businessName),
    businessNumber: stringSetting(source.businessNumber),
    defaultMemo: stringSetting(source.defaultMemo),
  };
}

function withOrderSmsSenderSettings(
  settings: Record<string, unknown> | undefined,
  sender: OrderSmsSenderSettings,
): Record<string, unknown> {
  const next = { ...(settings ?? {}) };
  const payload = {
    displayName: sender.displayName.trim(),
    phone: sender.phone.trim(),
    address: sender.address.trim(),
    businessName: sender.businessName.trim(),
    businessNumber: sender.businessNumber.trim(),
    defaultMemo: sender.defaultMemo.trim(),
  };
  const hasAnyValue = Object.values(payload).some((value) => value.length > 0);
  if (hasAnyValue) {
    next[ORDER_SMS_SENDER_SETTINGS_KEY] = payload;
  } else {
    delete next[ORDER_SMS_SENDER_SETTINGS_KEY];
  }
  return next;
}

function stringSetting(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function phoneDigits(value: string): string {
  return value.replace(/\D/g, '');
}

function isPhoneNumberText(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return phoneDigits(trimmed).length >= 8 && /^[\d\s+().-]+$/.test(trimmed);
}

function orderSmsSenderValidationError(sender: OrderSmsSenderSettings): string | null {
  if (isPhoneNumberText(sender.businessName)) {
    return '상호명에는 전화번호를 입력할 수 없습니다. 발주 담당 전화번호에 입력하세요.';
  }
  return null;
}

function platformColor(p: PlatformDto): string {
  // 시안 :66, :122, :168 — offline 만 earth 토큰 (브랜드 회색), 나머지는 p.color hex.
  return p.id === 'offline' ? OFFLINE_COLOR : p.color;
}

export function SettingsStoresRoute() {
  const auth = useAuth();
  const isOwner = auth.role === 'owner';
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Phase 6.B-3-redo hotfix — activePlat 은 null 시작, platforms·stores 로드 후
  // useEffect 로 첫 platform 또는 storeId 가 가리키는 platform 으로 동기화.
  // 단, render 단계에서도 derived fallback 을 적용해 첫 render 시 빈 list 가 보이지 않도록 한다.
  // Phase 7-E (2026-05-06) — 운영 root 알고리즘 사업장 계층 추가. 회사 → 사업장 → 플랫폼 → 상점.
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);
  const [activePlat, setActivePlat] = useState<string | null>(null);
  const [activeStoreId, setActiveStoreId] = useState<string | null>(
    searchParams.get('storeId'),
  );
  const [innerTab, setInnerTab] = useState<'menus' | 'info'>('menus');
  const [addStoreOpen, setAddStoreOpen] = useState(false);
  const [addLocationOpen, setAddLocationOpen] = useState(false);
  const [editLocation, setEditLocation] = useState<LocationRow | null>(null);
  const [addPlatformOpen, setAddPlatformOpen] = useState(false);
  const [editPlatformOpen, setEditPlatformOpen] = useState(false);

  // Phase 7-E (2026-05-06) — locations list (회사 사업장).
  const locationsQ = useQuery({
    queryKey: ['locations', 'list'],
    queryFn: async () => {
      const r = await locationsControllerList({
        query: { limit: '200' } as never,
      });
      return unwrap<LocationsEnvelope>(r);
    },
  });

  const locations = locationsQ.data?.items ?? [];
  const selectedLocationIdForChannels =
    activeLocationId ?? locations[0]?.id ?? null;

  // 선택 사업장에 연결된 판매채널 list. 회사 전체 플랫폼 목록을 직접 렌더링하지 않는다.
  const salesChannelsQ = useQuery({
    queryKey: ['locations', selectedLocationIdForChannels, 'sales-channels'],
    enabled: !!selectedLocationIdForChannels,
    queryFn: async () => {
      const r = await locationSalesChannelsControllerList({
        path: { locationId: selectedLocationIdForChannels! },
      });
      return unwrap<PlatformsEnvelope>(r);
    },
  });

  // 모든 매장 list — 한 번만 fetch 후 client-side platform / location 필터.
  const storesQ = useQuery({
    queryKey: ['stores', 'list'],
    queryFn: async () => {
      const r = await storesControllerList({
        query: { limit: '500' } as never,
      });
      return unwrap<StoresEnvelope>(r);
    },
  });

  const platforms = salesChannelsQ.data?.items ?? [];
  const allStores = storesQ.data?.items ?? [];

  const deleteLocationMu = useMutation({
    mutationFn: async (location: LocationRow) => {
      const r = await locationsControllerRemove({ path: { id: location.id } });
      return unwrap<{ deleted: boolean; id: string }>(r);
    },
    onSuccess: async (data) => {
      toast.success('사업장을 삭제했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['locations', 'list'] });
      await queryClient.invalidateQueries({ queryKey: ['stores', 'list'] });
      if (activeLocationId === data.id) {
        const next = locations.find((l) => l.id !== data.id)?.id ?? null;
        setActiveLocationId(next);
        setActivePlat(null);
        setActiveStoreId(null);
      }
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '사업장 삭제 실패');
    },
  });

  // Phase 7-E (2026-05-06) — 사업장 첫 로드 자동 선택.
  useEffect(() => {
    if (activeLocationId || locations.length === 0) return;
    // activeStoreId 가 가리키는 store 의 locationId 우선, 없으면 첫 사업장.
    const targetStore = activeStoreId
      ? allStores.find((s) => s.id === activeStoreId)
      : null;
    const next = targetStore?.locationId ?? locations[0]!.id;
    setActiveLocationId(next);
  }, [activeLocationId, locations, activeStoreId, allStores]);

  const effectiveLocationId =
    activeLocationId ?? locations[0]?.id ?? null;

  function requestLocationDelete(location: LocationRow, storeCount: number) {
    if (!isOwner) return;
    if (locations.length <= 1) {
      toast.error('마지막 사업장은 삭제할 수 없습니다.');
      return;
    }
    if (storeCount > 0) {
      toast.error('상점이 연결된 사업장은 삭제할 수 없습니다.');
      return;
    }
    if (window.confirm(`사업장 "${location.name}" 을 삭제하시겠습니까?`)) {
      deleteLocationMu.mutate(location);
    }
  }

  // 본 사업장 안 stores 만 처리 (= 플랫폼 탭·상점 list 모두 사업장 단위).
  const stores = useMemo(
    () =>
      effectiveLocationId
        ? allStores.filter((s) => s.locationId === effectiveLocationId)
        : [],
    [allStores, effectiveLocationId],
  );

  // 첫 로드 — activePlat 초기화 (첫 platform 또는 storeId 가 가리키는 platform)
  useEffect(() => {
    if (activePlat || platforms.length === 0) return;
    const targetStore = activeStoreId
      ? stores.find((s) => s.id === activeStoreId)
      : null;
    setActivePlat(targetStore?.platformId ?? platforms[0]!.id);
  }, [activePlat, platforms, activeStoreId, stores]);

  useEffect(() => {
    if (!activePlat) return;
    if (platforms.length === 0) {
      setActivePlat(null);
      setActiveStoreId(null);
      return;
    }
    if (!platforms.some((p) => p.id === activePlat)) {
      setActivePlat(platforms[0]!.id);
      setActiveStoreId(null);
    }
  }, [activePlat, platforms]);

  const platformById = useMemo(
    () => new Map(platforms.map((p) => [p.id, p])),
    [platforms],
  );
  const storesByPlatform = useMemo(() => {
    const map = new Map<string, StoreRow[]>();
    for (const s of stores) {
      if (!map.has(s.platformId)) map.set(s.platformId, []);
      map.get(s.platformId)!.push(s);
    }
    return map;
  }, [stores]);

  // Phase 6.B-3-redo hotfix — useEffect 가 commit 되기 전 첫 render 에서도
  // 빈 list 가 보이지 않도록 derived fallback 적용. activePlat 이 null 이면
  // 첫 platform 으로 가정하고 platStores 를 계산.
  const effectivePlat = activePlat ?? platforms[0]?.id ?? null;
  const platStores = effectivePlat
    ? storesByPlatform.get(effectivePlat) ?? []
    : [];
  const currentStore =
    platStores.find((s) => s.id === activeStoreId) ?? platStores[0] ?? null;

  // activePlat 변경 → 그 platform 의 첫 매장으로 sync (현재 store 가 다른 platform 에 속하면)
  useEffect(() => {
    if (!activePlat) return;
    if (!currentStore || currentStore.platformId !== activePlat) {
      const first = platStores[0];
      setActiveStoreId(first?.id ?? null);
    }
  }, [activePlat, platStores, currentStore]);

  // URL ?storeId 동기화
  useEffect(() => {
    if (activeStoreId) {
      if (searchParams.get('storeId') !== activeStoreId) {
        setSearchParams({ storeId: activeStoreId }, { replace: true });
      }
    } else if (searchParams.get('storeId')) {
      setSearchParams({}, { replace: true });
    }
  }, [activeStoreId, searchParams, setSearchParams]);

  if (salesChannelsQ.isLoading || storesQ.isLoading || locationsQ.isLoading) {
    return (
      <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>
        매장분류를 불러오는 중…
      </div>
    );
  }
  if (locationsQ.isError) {
    return (
      <AlertBanner
        tone="danger"
        message={`사업장 목록을 불러오지 못했습니다: ${
          (locationsQ.error as ApiCallError | undefined)?.message ?? '알 수 없는 오류'
        }`}
      />
    );
  }
  if (salesChannelsQ.isError) {
    return (
      <AlertBanner
        tone="danger"
        message={`판매채널 목록을 불러오지 못했습니다: ${
          (salesChannelsQ.error as ApiCallError | undefined)?.message ?? '알 수 없는 오류'
        }`}
      />
    );
  }
  if (storesQ.isError) {
    return (
      <AlertBanner
        tone="danger"
        message={`매장 목록을 불러오지 못했습니다: ${
          (storesQ.error as ApiCallError | undefined)?.message ?? '알 수 없는 오류'
        }`}
      />
    );
  }

  const plat = effectivePlat ? platformById.get(effectivePlat) : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHead
        title="매장분류"
        sub="사업장 안에 판매채널별 상점을 자유롭게 추가·변경할 수 있어요. 상점에서 만든 메뉴가 운영·분석·재고로 흘러갑니다."
      />

      {/* Phase 7-E (2026-05-06) — 사업장 탭 (회사 → 사업장 → 플랫폼 → 상점). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', letterSpacing: 0.4 }}>
          사업장
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {locations.map((l) => {
            const on = l.id === effectiveLocationId;
            const count = allStores.filter((s) => s.locationId === l.id).length;
            const deleteDisabled =
              !isOwner || deleteLocationMu.isPending || count > 0 || locations.length <= 1;
            const deleteTitle =
              count > 0
                ? '상점이 연결된 사업장은 삭제할 수 없습니다'
                : locations.length <= 1
                  ? '마지막 사업장은 삭제할 수 없습니다'
                  : '사업장을 삭제합니다';
            return (
              <div
                key={l.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '0 6px 0 0',
                  borderRadius: 10,
                  border: on ? '1.5px solid var(--accent)' : '1px solid var(--border-weak)',
                  background: on ? 'color-mix(in srgb, var(--accent) 8%, var(--bg-surface))' : 'var(--bg-surface)',
                  color: on ? 'var(--accent)' : 'var(--fg-2)',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setActiveLocationId(l.id);
                    setActivePlat(null);
                    setActiveStoreId(null);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 8px 8px 12px',
                    border: 0,
                    background: 'transparent',
                    color: 'inherit',
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  {l.name}
                  <span
                    style={{
                      padding: '1px 6px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                      background: on ? 'var(--accent)' : 'var(--bg-subtle)',
                      color: on ? '#fff' : 'var(--fg-3)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {count}
                  </span>
                </button>
                {isOwner && (
                  <>
                    <button
                      type="button"
                      title="사업장 이름 수정"
                      aria-label={`${l.name} 사업장 이름 수정`}
                      onClick={() => setEditLocation(l)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        borderRadius: 6,
                        border: '1px solid transparent',
                        background: 'transparent',
                        color: on ? 'var(--accent)' : 'var(--fg-3)',
                        cursor: 'pointer',
                      }}
                    >
                      <Icon name="edit" size={13} />
                    </button>
                    <button
                      type="button"
                      title={deleteTitle}
                      aria-label={`${l.name} 사업장 삭제`}
                      disabled={deleteDisabled}
                      onClick={() => requestLocationDelete(l, count)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        borderRadius: 6,
                        border: '1px solid transparent',
                        background: 'transparent',
                        color: deleteDisabled ? 'var(--fg-4)' : 'var(--danger)',
                        cursor: deleteDisabled ? 'not-allowed' : 'pointer',
                        opacity: deleteDisabled ? 0.5 : 1,
                      }}
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  </>
                )}
              </div>
            );
          })}
          <button
            type="button"
            disabled={!isOwner}
            onClick={() => setAddLocationOpen(true)}
            title={isOwner ? '새 사업장을 등록합니다' : 'owner 만 사업장을 추가할 수 있습니다'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              border: '1px dashed var(--border-weak)',
              background: 'transparent',
              color: isOwner ? 'var(--fg-2)' : 'var(--fg-4)',
              cursor: isOwner ? 'pointer' : 'not-allowed',
              fontFamily: 'var(--font-sans)',
            }}
          >
            <Icon name="plus" size={12} />
            사업장 추가
          </button>
        </div>
        {addLocationOpen && (
          <InlineAddLocationForm
            onClose={() => setAddLocationOpen(false)}
            onCreated={(id) => {
              setAddLocationOpen(false);
              setActiveLocationId(id);
              setActivePlat(null);
              setActiveStoreId(null);
            }}
          />
        )}
        <EditLocationModal
          open={!!editLocation}
          location={editLocation}
          onClose={() => setEditLocation(null)}
          onSaved={() => setEditLocation(null)}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', letterSpacing: 0.4 }}>
          판매채널
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {platforms.map((p) => {
            const on = p.id === effectivePlat;
            const color = platformColor(p);
            const count = storesByPlatform.get(p.id)?.length ?? 0;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setActivePlat(p.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  border: on ? `1.5px solid ${color}` : '1px solid var(--border-weak)',
                  background: on ? `${color}10` : 'var(--bg-surface)',
                  color: on ? color : 'var(--fg-2)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: color,
                  }}
                />
                {p.name}
                <span
                  style={{
                    padding: '1px 6px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    background: on ? color : 'var(--bg-subtle)',
                    color: on ? '#fff' : 'var(--fg-3)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            disabled={!isOwner || !effectiveLocationId}
            onClick={() => setAddPlatformOpen(true)}
            title={isOwner ? '선택한 사업장에 판매채널을 생성합니다' : 'owner 만 판매채널을 추가할 수 있습니다'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              border: '1px dashed var(--primary-border)',
              background: 'var(--primary-bg)',
              color: 'var(--primary-strong)',
              cursor: isOwner && effectiveLocationId ? 'pointer' : 'not-allowed',
              opacity: isOwner && effectiveLocationId ? 1 : 0.55,
              fontFamily: 'var(--font-sans)',
            }}
          >
            + 판매채널 생성
          </button>
        </div>
      </div>

      {/* 2-column grid */}
      {plat && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '260px 1fr',
            gap: 16,
            alignItems: 'start',
          }}
        >
          {/* Stores list card */}
          <div
            style={{
              padding: 0,
              minHeight: 420,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-weak)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <div
              style={{
                padding: '12px 14px',
                borderBottom: '1px solid var(--border-weak)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>
                  {plat.name} · 상점{' '}
                  <span style={{ color: 'var(--fg-3)', fontWeight: 500 }}>
                    ({platStores.length})
                  </span>
                </div>
                {/* Phase 6.B-7 — 수수료·배달비 표시 (분석 차감용) */}
                {(plat.commissionRate > 0 || plat.deliveryFee > 0) && (
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--fg-3)',
                      marginTop: 2,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {plat.commissionRate > 0 &&
                      `수수료 ${(plat.commissionRate * 100).toFixed(plat.commissionRate * 100 % 1 === 0 ? 0 : 2)}%`}
                    {plat.commissionRate > 0 && plat.deliveryFee > 0 && ' · '}
                    {plat.deliveryFee > 0 &&
                      `배달비 ${plat.deliveryFee.toLocaleString('ko-KR')}원`}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button
                  type="button"
                  disabled={!isOwner}
                  onClick={() => setEditPlatformOpen(true)}
                  title={isOwner ? '판매채널 편집 (수수료·배달비)' : 'owner 만 편집할 수 있습니다'}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 8px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--fg-2)',
                    background: 'transparent',
                    border: '1px solid var(--border-base)',
                    borderRadius: 6,
                    cursor: isOwner ? 'pointer' : 'not-allowed',
                    opacity: isOwner ? 1 : 0.55,
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  ✏️ 편집
                </button>
                <button
                  type="button"
                  disabled={!isOwner}
                  onClick={() => setAddStoreOpen(true)}
                  title={isOwner ? '' : 'owner 만 매장을 추가할 수 있습니다'}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 8px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--primary-strong)',
                    background: 'var(--primary-bg)',
                    border: '1px solid var(--primary-border)',
                    borderRadius: 6,
                    cursor: isOwner ? 'pointer' : 'not-allowed',
                    opacity: isOwner ? 1 : 0.55,
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  + 추가
                </button>
              </div>
            </div>
            <div
              style={{
                padding: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                maxHeight: 480,
                overflowY: 'auto',
              }}
            >
              {platStores.length === 0 && !addStoreOpen && (
                <div
                  style={{
                    padding: '24px 12px',
                    textAlign: 'center',
                    color: 'var(--fg-3)',
                    fontSize: 12,
                  }}
                >
                  이 판매채널에 등록된 상점이 없습니다.
                </div>
              )}
              {platStores.map((s) => {
                const on = s.id === currentStore?.id;
                const color = platformColor(plat);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveStoreId(s.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 8,
                      textAlign: 'left',
                      background: on ? `${color}12` : 'transparent',
                      border: on
                        ? `1px solid ${color}55`
                        : '1px solid transparent',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-sans)',
                    }}
                  >
                    <Icon
                      name="store"
                      size={15}
                      color={on ? color : 'var(--fg-3)'}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: on ? 600 : 500,
                          color: 'var(--fg-1)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {s.brand ?? s.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--fg-3)',
                          marginTop: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {s.externalSiteId
                          ? `ID ${s.externalSiteId}`
                          : '외부 ID 미연결'}
                      </div>
                    </div>
                    <StatusDot tone={s.status === 'active' ? 'success' : 'muted'} />
                  </button>
                );
              })}

              {addStoreOpen && (
                <InlineAddStoreForm
                  platform={plat}
                  locationId={effectiveLocationId}
                  onClose={() => setAddStoreOpen(false)}
                  onCreated={(id) => {
                    setActiveStoreId(id);
                    setAddStoreOpen(false);
                  }}
                />
              )}
            </div>
          </div>

          {/* Store detail card */}
          <div
            style={{
              padding: 0,
              minHeight: 420,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-weak)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            {currentStore ? (
              <StoreDetailPane
                store={currentStore}
                platform={plat}
                tab={innerTab}
                onTab={setInnerTab}
                isOwner={isOwner}
                onDeleted={() => {
                  setActiveStoreId(null);
                }}
              />
            ) : (
              <EmptyState
                icon="store"
                title="매장을 선택하거나 추가하세요"
                description={`${plat.name} 에 등록된 매장이 없습니다. 좌측에서 "+ 추가" 로 첫 매장을 등록하세요.`}
              />
            )}
          </div>
        </div>
      )}

      {!plat && (
        <EmptyState
          icon="store"
          title="등록된 판매채널이 없습니다"
          description={
            isOwner
              ? '판매채널 생성으로 이 사업장에서 사용할 판매채널을 먼저 등록하세요.'
              : 'owner 가 판매채널을 등록한 뒤 상점을 추가할 수 있습니다.'
          }
        />
      )}

      <AddPlatformModal
        open={addPlatformOpen}
        locationId={effectiveLocationId}
        onClose={() => setAddPlatformOpen(false)}
        onCreated={(id) => {
          setActivePlat(id);
          setAddPlatformOpen(false);
        }}
      />
      <EditPlatformModal
        open={editPlatformOpen}
        platform={plat ?? null}
        locationId={effectiveLocationId}
        onClose={() => setEditPlatformOpen(false)}
        onSaved={() => setEditPlatformOpen(false)}
        onDeleted={() => {
          setEditPlatformOpen(false);
          setActivePlat(null);
        }}
      />
    </div>
  );
}

// ─── inline add-store form (시안 :190-211 패턴) ───────────────────────
function InlineAddStoreForm({
  platform,
  locationId,
  onClose,
  onCreated,
}: {
  platform: PlatformDto;
  /// Phase 7-E (2026-05-06) — 본 상점이 매핑될 사업장 (= 선택된 사업장).
  locationId: string | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [externalSiteId, setExternalSiteId] = useState('');

  const createMu = useMutation({
    mutationFn: async () => {
      const r = await storesControllerCreate({
        body: {
          name: name.trim(),
          platformId: platform.id as never,
          ...(locationId ? { locationId } : {}),
          ...(externalSiteId.trim()
            ? { externalSiteId: externalSiteId.trim() }
            : {}),
        } as never,
      });
      return unwrap<{ item: StoreRow }>(r);
    },
    onSuccess: async (data) => {
      toast.success('매장을 추가했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['stores', 'list'] });
      onCreated(data.item.id);
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '매장 등록 실패');
    },
  });

  function submit() {
    if (!name.trim()) {
      toast.error('상점 이름은 필수입니다.');
      return;
    }
    createMu.mutate();
  }

  return (
    <div
      style={{
        margin: '8px 4px',
        padding: 10,
        borderRadius: 8,
        border: '1px dashed var(--primary-border)',
        background: 'var(--primary-bg)',
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--primary-strong)',
          marginBottom: 6,
        }}
      >
        새 상점 등록
      </div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="상점 이름 (예: 고기특별시)"
        style={{
          width: '100%',
          padding: '7px 10px',
          fontSize: 13,
          borderRadius: 6,
          border: '1px solid var(--border-base)',
          background: '#fff',
          marginBottom: 6,
          fontFamily: 'var(--font-sans)',
          boxSizing: 'border-box',
        }}
      />
      <input
        value={externalSiteId}
        onChange={(e) => setExternalSiteId(e.target.value)}
        placeholder={`${platform.name} 사장님 ID (선택)`}
        style={{
          width: '100%',
          padding: '7px 10px',
          fontSize: 13,
          borderRadius: 6,
          border: '1px solid var(--border-base)',
          background: '#fff',
          marginBottom: 8,
          fontFamily: 'var(--font-sans)',
          boxSizing: 'border-box',
        }}
      />
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onClose}
          disabled={createMu.isPending}
          style={{
            padding: '5px 10px',
            fontSize: 12,
            border: '1px solid var(--border-base)',
            borderRadius: 6,
            background: '#fff',
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
          }}
        >
          취소
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={createMu.isPending}
          style={{
            padding: '5px 10px',
            fontSize: 12,
            fontWeight: 600,
            border: '1px solid var(--primary-strong)',
            borderRadius: 6,
            background: 'var(--primary-strong)',
            color: '#fff',
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {createMu.isPending ? '등록 중…' : '등록'}
        </button>
      </div>
    </div>
  );
}

function OrderSmsSenderFields({
  value,
  onChange,
}: {
  value: OrderSmsSenderSettings;
  onChange: (next: OrderSmsSenderSettings) => void;
}) {
  const businessNameError = isPhoneNumberText(value.businessName)
    ? '상호명에는 전화번호를 입력할 수 없습니다.'
    : undefined;

  function setField(field: keyof OrderSmsSenderSettings, nextValue: string) {
    onChange({ ...value, [field]: nextValue });
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: 12,
        border: '1px solid var(--border-weak)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--bg-subtle)',
        minWidth: 0,
      }}
    >
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-1)' }}>
          발주/SMS 발신 정보
        </div>
        <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 3 }}>
          거래처가 문자만 보고 어느 사업장의 발주인지 알 수 있게 SMS 본문에 포함됩니다.
        </div>
      </div>

      <FormField label="발주 표시명">
        <TextInput
          value={value.displayName}
          onChange={(next) => setField('displayName', next)}
          placeholder="예: 비전 아토푸드 세교점"
        />
      </FormField>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 10,
          minWidth: 0,
        }}
      >
        <FormField label="발주 담당 전화번호">
          <TextInput
            type="tel"
            value={value.phone}
            onChange={(next) => setField('phone', next)}
            placeholder="010-0000-0000"
          />
        </FormField>
        <FormField label="상호명" error={businessNameError}>
          <TextInput
            value={value.businessName}
            onChange={(next) => setField('businessName', next)}
            placeholder="예: 비전 아토푸드"
            error={!!businessNameError}
          />
        </FormField>
      </div>

      <FormField label="주소">
        <TextInput
          value={value.address}
          onChange={(next) => setField('address', next)}
          placeholder="납품처 주소"
        />
      </FormField>

      <FormField label="사업자번호">
        <TextInput
          value={value.businessNumber}
          onChange={(next) => setField('businessNumber', next)}
          placeholder="선택 입력"
        />
      </FormField>

      <FormField label="기본 요청 메모">
        <textarea
          value={value.defaultMemo}
          onChange={(event) => setField('defaultMemo', event.target.value)}
          placeholder="예: 오전 배송 요청"
          rows={2}
          style={{
            width: '100%',
            minHeight: 66,
            resize: 'vertical',
            padding: '8px 10px',
            fontSize: 13,
            lineHeight: 1.45,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border-base)',
            background: '#fff',
            color: 'var(--fg-1)',
            fontFamily: 'var(--font-sans)',
            boxSizing: 'border-box',
          }}
        />
      </FormField>
    </div>
  );
}

// Phase 7-E (2026-05-06) — 사업장 inline add form (= InlineAddStoreForm 패턴 참조).
function InlineAddLocationForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [orderSmsSender, setOrderSmsSender] = useState<OrderSmsSenderSettings>(
    emptyOrderSmsSenderSettings,
  );

  const createMu = useMutation({
    mutationFn: async () => {
      const r = await locationsControllerCreate({
        body: {
          name: name.trim(),
          settings: withOrderSmsSenderSettings(undefined, orderSmsSender),
        } as never,
      });
      return unwrap<{ item: LocationRow }>(r);
    },
    onSuccess: async (data) => {
      toast.success('사업장을 추가했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['locations', 'list'] });
      onCreated(data.item.id);
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '사업장 등록 실패');
    },
  });

  function submit() {
    if (!name.trim()) {
      toast.error('사업장 이름은 필수입니다.');
      return;
    }
    const senderError = orderSmsSenderValidationError(orderSmsSender);
    if (senderError) {
      toast.error(senderError);
      return;
    }
    createMu.mutate();
  }

  return (
    <div
      style={{
        marginTop: 4,
        padding: 10,
        borderRadius: 8,
        border: '1px dashed var(--primary-border)',
        background: 'var(--primary-bg)',
        maxWidth: 720,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--primary-strong)',
          marginBottom: 6,
        }}
      >
        새 사업장 등록
      </div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="사업장 이름 (예: 강남 1호점)"
        style={{
          width: '100%',
          padding: '7px 10px',
          fontSize: 13,
          borderRadius: 6,
          border: '1px solid var(--border-base)',
          background: '#fff',
          marginBottom: 8,
          fontFamily: 'var(--font-sans)',
          boxSizing: 'border-box',
        }}
      />
      <OrderSmsSenderFields value={orderSmsSender} onChange={setOrderSmsSender} />
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
        <button
          type="button"
          onClick={onClose}
          disabled={createMu.isPending}
          style={{
            padding: '5px 10px',
            fontSize: 12,
            border: '1px solid var(--border-base)',
            borderRadius: 6,
            background: '#fff',
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
          }}
        >
          취소
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={createMu.isPending}
          style={{
            padding: '5px 10px',
            fontSize: 12,
            fontWeight: 600,
            border: '1px solid var(--primary-strong)',
            borderRadius: 6,
            background: 'var(--primary-strong)',
            color: '#fff',
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {createMu.isPending ? '등록 중…' : '등록'}
        </button>
      </div>
    </div>
  );
}

function EditLocationModal({
  open,
  location,
  onClose,
  onSaved,
}: {
  open: boolean;
  location: LocationRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [orderSmsSender, setOrderSmsSender] = useState<OrderSmsSenderSettings>(
    emptyOrderSmsSenderSettings,
  );

  useEffect(() => {
    if (!open || !location) return;
    setName(location.name);
    setOrderSmsSender(readOrderSmsSenderSettings(location.settings));
  }, [open, location]);

  const updateMu = useMutation({
    mutationFn: async () => {
      if (!location) throw new Error('location missing');
      const r = await locationsControllerUpdate({
        path: { id: location.id },
        body: {
          name: name.trim(),
          settings: withOrderSmsSenderSettings(location.settings, orderSmsSender),
        } as never,
      });
      return unwrap<{ item: LocationRow }>(r);
    },
    onSuccess: async () => {
      toast.success('사업장을 저장했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['locations', 'list'] });
      onSaved();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '사업장 저장 실패');
    },
  });

  function submit() {
    if (!location) return;
    if (!name.trim()) {
      toast.error('사업장 이름은 필수입니다.');
      return;
    }
    const senderError = orderSmsSenderValidationError(orderSmsSender);
    if (senderError) {
      toast.error(senderError);
      return;
    }
    updateMu.mutate();
  }

  if (!location) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`사업장 수정 — ${location.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updateMu.isPending}>
            취소
          </Button>
          <Button variant="strong" onClick={submit} disabled={updateMu.isPending}>
            {updateMu.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FormField label="사업장 이름" required>
          <TextInput value={name} onChange={setName} placeholder="강남 1호점" />
        </FormField>
        <OrderSmsSenderFields value={orderSmsSender} onChange={setOrderSmsSender} />
      </div>
    </Modal>
  );
}

// ─── add sales channel modal (owner only, selected-location scoped) ───
function AddPlatformModal({
  open,
  locationId,
  onClose,
  onCreated,
}: {
  open: boolean;
  locationId: string | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!open) return;
    setName('');
    setError(undefined);
  }, [open]);

  const createMu = useMutation({
    mutationFn: async () => {
      if (!locationId) throw new Error('location missing');
      const r = await locationSalesChannelsControllerCreate({
        path: { locationId },
        body: { name: name.trim() } as never,
      });
      return unwrap<{ item: PlatformDto }>(r);
    },
    onSuccess: async (data) => {
      toast.success(`판매채널 "${data.item.name}" 을 생성했습니다.`);
      if (locationId) {
        await queryClient.invalidateQueries({
          queryKey: ['locations', locationId, 'sales-channels'],
        });
      }
      onCreated(data.item.id);
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '판매채널 생성 실패');
    },
  });

  function submit() {
    if (!locationId) {
      toast.error('사업장을 먼저 선택하세요.');
      return;
    }
    if (!name.trim() || name.trim().length > 40) {
      setError('1-40자 이름을 입력하세요.');
      return;
    }
    setError(undefined);
    createMu.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="판매채널 생성"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={createMu.isPending}>
            취소
          </Button>
          <Button variant="strong" onClick={submit} disabled={createMu.isPending}>
            {createMu.isPending ? '등록 중…' : '등록'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FormField label="판매채널 이름" required error={error}>
          <TextInput
            value={name}
            onChange={setName}
            placeholder="판매채널 이름"
            error={!!error}
          />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── edit platform modal (owner only, company-scoped, Phase 6.B-7) ─────
function EditPlatformModal({
  open,
  platform,
  locationId,
  onClose,
  onSaved,
  onDeleted,
}: {
  open: boolean;
  platform: PlatformDto | null;
  locationId: string | null;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [color, setColor] = useState('#34d399');
  const [commissionRatePct, setCommissionRatePct] = useState('');
  const [deliveryFee, setDeliveryFee] = useState('');
  const [errors, setErrors] = useState<{
    name?: string;
    shortName?: string;
    color?: string;
    commissionRate?: string;
    deliveryFee?: string;
  }>({});

  useEffect(() => {
    if (!open || !platform) return;
    setName(platform.name);
    setShortName(platform.shortName);
    setColor(platform.color);
    setCommissionRatePct(
      platform.commissionRate ? String(platform.commissionRate * 100) : '',
    );
    setDeliveryFee(platform.deliveryFee ? String(platform.deliveryFee) : '');
    setErrors({});
  }, [open, platform]);

  const updateMu = useMutation({
    mutationFn: async () => {
      if (!platform) throw new Error('platform missing');
      const cr = commissionRatePct.trim()
        ? Number(commissionRatePct.trim()) / 100
        : 0;
      const df = deliveryFee.trim() ? Math.round(Number(deliveryFee.trim())) : 0;
      const r = await platformsControllerUpdate({
        path: { id: platform.id },
        body: {
          name: name.trim(),
          shortName: shortName.trim(),
          color: color.trim(),
          commissionRate: cr,
          deliveryFee: df,
        } as never,
      });
      return unwrap<{ item: PlatformDto }>(r);
    },
    onSuccess: async () => {
      toast.success('판매채널을 저장했습니다.');
      if (locationId) {
        await queryClient.invalidateQueries({
          queryKey: ['locations', locationId, 'sales-channels'],
        });
      }
      onSaved();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '판매채널 저장 실패');
    },
  });

  const removeMu = useMutation({
    mutationFn: async () => {
      if (!platform) throw new Error('platform missing');
      if (!locationId) throw new Error('location missing');
      const r = await locationSalesChannelsControllerRemove({
        path: { locationId, platformId: platform.id },
      });
      return unwrap<{ deleted: boolean; locationId: string; platformId: string }>(r);
    },
    onSuccess: async () => {
      toast.success('판매채널 연결을 해제했습니다.');
      if (locationId) {
        await queryClient.invalidateQueries({
          queryKey: ['locations', locationId, 'sales-channels'],
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['stores', 'list'] });
      onDeleted();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '판매채널 연결 해제 실패');
    },
  });

  function submit() {
    if (!platform) return;
    const next: typeof errors = {};
    if (!name.trim() || name.trim().length > 40) next.name = '1-40자 한글/영문 이름';
    if (!shortName.trim() || shortName.trim().length > 8)
      next.shortName = '1-8자 단축이름';
    if (!/^#[0-9a-fA-F]{6}$/.test(color.trim()))
      next.color = '#RRGGBB 6자리 hex';
    if (commissionRatePct.trim()) {
      const cr = Number(commissionRatePct.trim());
      if (!Number.isFinite(cr) || cr < 0 || cr >= 100)
        next.commissionRate = '0 이상 100 미만';
    }
    if (deliveryFee.trim()) {
      const df = Number(deliveryFee.trim());
      if (!Number.isFinite(df) || df < 0) next.deliveryFee = '0 이상 정수';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    updateMu.mutate();
  }

  function deleteWithConfirm() {
    if (!platform) return;
    if (!locationId) {
      toast.error('사업장을 먼저 선택하세요.');
      return;
    }
    if (
      window.confirm(
        `이 사업장에서 "${platform.name}" 판매채널 연결을 해제하시겠습니까?\n상점이 있으면 해제되지 않습니다.`,
      )
    ) {
      removeMu.mutate();
    }
  }

  if (!platform) return null;
  const pending = updateMu.isPending || removeMu.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`판매채널 편집 — ${platform.name}`}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={deleteWithConfirm}
            disabled={pending}
            style={{ color: 'var(--danger)', marginRight: 'auto' }}
          >
            {removeMu.isPending ? '해제 중…' : '연결 해제'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            취소
          </Button>
          <Button variant="strong" onClick={submit} disabled={pending}>
            {updateMu.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <AlertBanner
          tone="info"
          message="수수료율·배달비는 현재 회사의 분석에서 자동 차감됩니다."
        />
        <FormField label="판매채널 ID (변경 불가)" hint="시스템 내부 키">
          <TextInput value={platform.id} onChange={() => {}} disabled />
        </FormField>
        <FormField label="이름" required error={errors.name}>
          <TextInput value={name} onChange={setName} error={!!errors.name} />
        </FormField>
        <FormField label="단축이름 (탭 표시)" required error={errors.shortName}>
          <TextInput
            value={shortName}
            onChange={setShortName}
            error={!!errors.shortName}
          />
        </FormField>
        <FormField label="색상" required error={errors.color}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <TextInput value={color} onChange={setColor} error={!!errors.color} />
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: color,
                border: '1px solid var(--border-weak)',
              }}
            />
          </div>
        </FormField>
        <FormField
          label="수수료율 (%)"
          error={errors.commissionRate}
          hint="판매채널이 매출에서 가져가는 비율"
        >
          <TextInput
            value={commissionRatePct}
            onChange={setCommissionRatePct}
            placeholder="예: 15"
            type="number"
            error={!!errors.commissionRate}
          />
        </FormField>
        <FormField
          label="배달비 (원)"
          error={errors.deliveryFee}
          hint="주문 1건당 차감되는 배달비"
        >
          <TextInput
            value={deliveryFee}
            onChange={setDeliveryFee}
            placeholder="예: 3000"
            type="number"
            error={!!errors.deliveryFee}
          />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── store detail (header + SubNav + tab body) ────────────────────────
function StoreDetailPane({
  store,
  platform,
  tab,
  onTab,
  isOwner,
  onDeleted,
}: {
  store: StoreRow;
  platform: PlatformDto;
  tab: 'menus' | 'info';
  onTab: (id: 'menus' | 'info') => void;
  isOwner: boolean;
  onDeleted: () => void;
}) {
  const toast = useToast();
  const color = platformColor(platform);

  const removeMu = useMutation({
    mutationFn: async () => {
      const r = await storesControllerRemove({ path: { id: store.id } });
      return unwrap<{ deleted: boolean; id: string }>(r);
    },
    onSuccess: async () => {
      toast.success('매장을 삭제했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['stores', 'list'] });
      onDeleted();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '매장 삭제 실패');
    },
  });

  const subItems: SubNavItem[] = [
    { id: 'menus', label: '메뉴등록' },
    { id: 'info', label: '상점 정보' },
  ];

  return (
    <>
      {/* Store header (시안 :240-253) */}
      <div
        style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--border-weak)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: `${color}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="store" size={16} color={color} />
        </span>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--fg-1)',
              letterSpacing: '-0.01em',
            }}
          >
            {store.brand ?? store.name}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--fg-3)',
              marginTop: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <PlatformBadge p={platform} />
            <span>· {store.name}</span>
            <Badge variant={store.status === 'active' ? 'success' : 'default'}>
              {store.status === 'active' ? '운영중' : '점검중'}
            </Badge>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={!isOwner || removeMu.isPending}
          onClick={() => {
            if (
              window.confirm(
                `정말 "${store.brand ?? store.name}" 매장을 삭제하시겠습니까?\n관련된 메뉴 · 레시피도 모두 삭제됩니다.`,
              )
            ) {
              removeMu.mutate();
            }
          }}
          style={{ color: 'var(--danger)' }}
        >
          삭제
        </Button>
      </div>

      {/* SubNav (메뉴등록 + 상점정보) */}
      <div
        style={{
          padding: '12px 18px 0',
          borderBottom: '1px solid var(--border-weak)',
        }}
      >
        <SubNav
          items={subItems}
          active={tab}
          onChange={(id) => onTab(id as 'menus' | 'info')}
        />
        <div style={{ height: 12 }} />
      </div>

      {tab === 'menus' && <MenusTab store={store} isOwner={isOwner} />}
      {tab === 'info' && (
        <StoreInfoTab store={store} platform={platform} isOwner={isOwner} />
      )}
    </>
  );
}

function PlatformBadge({ p }: { p: PlatformDto }) {
  const color = platformColor(p);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        borderRadius: 999,
        background: `${color}18`,
        color: color,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      <span
        style={{ width: 6, height: 6, borderRadius: 999, background: color }}
      />
      {p.shortName}
    </span>
  );
}

// ─── MenusTab — Phase 6.D-5: StoreMenu link 패턴 (마스터 메뉴 검색 + 매장별 단가 override) ───
//
// 6.D-5 변경 (사용자 요구):
//   - 매장에 메뉴 직접 create (X) → 회사 마스터 메뉴 검색 + StoreMenu link (O)
//   - + 메뉴 추가 modal: MasterMenuSearchInput 으로 마스터 메뉴 선택 → unitPrice override 입력
//   - 편집: unitPrice override / externalMenuId / isActive 만
//   - 삭제: unlink (StoreMenu 만 제거, Menu 마스터 보존)
//
const MENU_TABLE_COLS = 'minmax(180px,1.4fr) 120px 130px 80px 90px';

function MenusTab({ store, isOwner }: { store: StoreRow; isOwner: boolean }) {
  const toast = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<StoreMenuRow | null>(null);

  const storeMenusQ = useQuery({
    queryKey: ['store-menus', { storeId: store.id }],
    queryFn: async () => {
      const r = await storeMenusControllerList({
        path: { storeId: store.id },
        query: { limit: '200' } as never,
      });
      return unwrap<StoreMenusEnvelope>(r);
    },
  });

  const removeMu = useMutation({
    mutationFn: async (id: string) => {
      const r = await storeMenusControllerRemove({ path: { id } });
      return unwrap<{ deleted: boolean; id: string }>(r);
    },
    onSuccess: async () => {
      toast.success('매장 메뉴를 해제했습니다 (마스터 메뉴는 유지).');
      await queryClient.invalidateQueries({
        queryKey: ['store-menus', { storeId: store.id }],
      });
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '매장 메뉴 해제 실패');
    },
  });

  const items = storeMenusQ.data?.items ?? [];

  return (
    <div style={{ padding: '16px 18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--fg-3)', lineHeight: 1.5, maxWidth: 480 }}>
          이 매장에서 판매할 메뉴를 등록하세요. 회사 마스터 메뉴(설정 &gt; 레시피에서 추가)를 선택하고
          매장별 단가 override 를 지정할 수 있어요.
        </div>
        <Button
          variant="primary"
          size="sm"
          disabled={!isOwner}
          title={isOwner ? '' : 'owner 만 메뉴를 추가할 수 있습니다'}
          onClick={() => setAddOpen(true)}
        >
          + 메뉴 등록
        </Button>
      </div>

      {storeMenusQ.isLoading ? (
        <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>
          매장 메뉴 목록을 불러오는 중…
        </div>
      ) : storeMenusQ.isError ? (
        <AlertBanner
          tone="danger"
          message={`매장 메뉴 목록을 불러오지 못했습니다: ${
            (storeMenusQ.error as ApiCallError | undefined)?.message ?? '알 수 없는 오류'
          }`}
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon="receipt"
          title="등록된 매장 메뉴가 없습니다"
          description="회사 마스터 메뉴를 검색하여 이 매장에 등록하세요. 마스터 메뉴는 설정 > 레시피에서 추가할 수 있습니다."
          action={
            isOwner ? (
              <Button variant="primary" onClick={() => setAddOpen(true)}>
                + 메뉴 등록
              </Button>
            ) : null
          }
        />
      ) : (
        <TableShell>
          <TableHeader columns={MENU_TABLE_COLS}>
            <div>메뉴명 · 분류</div>
            <div style={{ textAlign: 'right' }}>매장 단가</div>
            <div>외부 메뉴 ID</div>
            <div style={{ textAlign: 'center' }}>상태</div>
            <div></div>
          </TableHeader>
          {items.map((sm, i) => {
            const effectivePrice =
              sm.unitPrice ?? sm.menu?.unitPrice ?? '0';
            const overridden = sm.unitPrice != null;
            return (
              <TableRow key={sm.id} columns={MENU_TABLE_COLS} first={i === 0}>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      color: 'var(--fg-1)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sm.menu?.name ?? '(메뉴 없음)'}
                  </div>
                  {sm.menu?.categoryName && (
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--fg-3)',
                        marginTop: 1,
                      }}
                    >
                      {sm.menu.categoryName}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    color: 'var(--fg-1)',
                  }}
                >
                  {formatWon(effectivePrice)}
                  {overridden && (
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--primary-strong)',
                        marginTop: 1,
                      }}
                    >
                      override
                    </div>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--fg-3)',
                    fontFamily: 'var(--font-mono)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {sm.externalMenuId ?? '—'}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Badge variant={sm.isActive ? 'success' : 'default'}>
                    {sm.isActive ? '운영' : '중지'}
                  </Badge>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 4,
                  }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!isOwner}
                    onClick={() => setEditTarget(sm)}
                  >
                    편집
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!isOwner || removeMu.isPending}
                    onClick={() => {
                      const name = sm.menu?.name ?? '메뉴';
                      if (
                        window.confirm(
                          `"${name}" 을(를) 이 매장에서 해제하시겠습니까? (마스터 메뉴는 유지)`,
                        )
                      ) {
                        removeMu.mutate(sm.id);
                      }
                    }}
                    style={{ color: 'var(--danger)' }}
                  >
                    해제
                  </Button>
                </div>
              </TableRow>
            );
          })}
        </TableShell>
      )}

      <StoreMenuAddModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        storeId={store.id}
        existingMenuIds={new Set(items.map((sm) => sm.menuId))}
      />
      <StoreMenuEditModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        storeId={store.id}
        target={editTarget}
      />
    </div>
  );
}

// ─── 마스터 메뉴 검색 input (debounce 300ms ILIKE) ──────────────────────
function MasterMenuSearchInput({
  menus,
  selectedId,
  onSelect,
  excludeIds,
  disabled,
  error,
}: {
  menus: MenuRow[];
  selectedId: string;
  onSelect: (m: MenuRow) => void;
  excludeIds?: Set<string>;
  disabled?: boolean;
  error?: boolean;
}) {
  const selected = menus.find((m) => m.id === selectedId);
  const [query, setQuery] = useState(selected?.name ?? '');
  const [debounced, setDebounced] = useState(query);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(selected?.name ?? '');
  }, [selected?.id, selected?.name]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = debounced.toLowerCase();
    let list = menus;
    if (excludeIds && excludeIds.size > 0) {
      list = list.filter((m) => !excludeIds.has(m.id) || m.id === selectedId);
    }
    if (!q) return list.slice(0, 40);
    return list
      .filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          (m.categoryName ?? '').toLowerCase().includes(q),
      )
      .slice(0, 40);
  }, [menus, debounced, excludeIds, selectedId]);

  useEffect(() => {
    setHighlight(0);
  }, [debounced, open]);

  function pick(m: MenuRow) {
    onSelect(m);
    setQuery(m.name);
    setOpen(false);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
            setOpen(true);
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(filtered.length - 1, h + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const m = filtered[highlight];
            if (m) pick(m);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        placeholder="회사 마스터 메뉴 검색…"
        disabled={disabled}
        style={{
          width: '100%',
          padding: '7px 10px',
          fontSize: 13,
          fontFamily: 'var(--font-sans)',
          color: 'var(--fg-1)',
          background: disabled ? 'var(--bg-subtle)' : 'var(--bg-surface)',
          border: `1px solid ${error ? 'var(--danger)' : 'var(--border-base)'}`,
          borderRadius: 'var(--radius-sm)',
          outline: 'none',
        }}
      />
      {open && filtered.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-base)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 50,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {filtered.map((m, i) => {
            const on = i === highlight;
            const sel = m.id === selectedId;
            return (
              <button
                key={m.id}
                type="button"
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(m);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '7px 10px',
                  fontSize: 13,
                  fontFamily: 'var(--font-sans)',
                  textAlign: 'left',
                  background: on
                    ? 'var(--primary-bg)'
                    : sel
                      ? 'var(--bg-subtle)'
                      : 'transparent',
                  border: 'none',
                  borderBottom: '1px solid var(--border-weak)',
                  cursor: 'pointer',
                  color: 'var(--fg-1)',
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontWeight: sel ? 600 : 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                    {m.categoryName ?? '카테고리 없음'} · {formatWon(m.unitPrice)}
                  </div>
                </div>
                {sel && <Icon name="check" size={14} color="var(--primary-strong)" />}
              </button>
            );
          })}
        </div>
      )}
      {open && filtered.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            padding: '10px 12px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-base)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-md)',
            fontSize: 12,
            color: 'var(--fg-3)',
            zIndex: 50,
          }}
        >
          일치하는 마스터 메뉴가 없습니다. 설정 &gt; 레시피에서 먼저 메뉴를 추가하세요.
        </div>
      )}
    </div>
  );
}

// ─── + 메뉴 등록 Modal (마스터 검색 + StoreMenu create) ────────────────
function StoreMenuAddModal({
  open,
  onClose,
  storeId,
  existingMenuIds,
}: {
  open: boolean;
  onClose: () => void;
  storeId: string;
  existingMenuIds: Set<string>;
}) {
  const toast = useToast();
  const [menuId, setMenuId] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [externalMenuId, setExternalMenuId] = useState('');
  const [errors, setErrors] = useState<{ menuId?: string; unitPrice?: string }>(
    {},
  );

  const masterQ = useQuery({
    queryKey: ['menus', 'master', { limit: 500 }],
    queryFn: async () => {
      const r = await menusControllerListMaster({
        query: { limit: '500' } as never,
      });
      return unwrap<MasterMenusEnvelope>(r);
    },
    enabled: open,
  });

  const menus = masterQ.data?.items ?? [];
  const selectedMenu = menus.find((m) => m.id === menuId) ?? null;

  useEffect(() => {
    if (!open) return;
    setMenuId('');
    setUnitPrice('');
    setExternalMenuId('');
    setErrors({});
  }, [open]);

  const createMu = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { menuId };
      if (unitPrice.trim()) body.unitPrice = Number(unitPrice);
      if (externalMenuId.trim()) body.externalMenuId = externalMenuId.trim();
      const r = await storeMenusControllerCreate({
        path: { storeId },
        body: body as never,
      });
      return unwrap<{ item: StoreMenuRow }>(r);
    },
    onSuccess: async () => {
      toast.success('매장 메뉴를 등록했습니다.');
      await queryClient.invalidateQueries({
        queryKey: ['store-menus', { storeId }],
      });
      onClose();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '매장 메뉴 등록 실패');
    },
  });

  function submit() {
    const next: typeof errors = {};
    if (!menuId) next.menuId = '회사 마스터 메뉴를 선택하세요.';
    if (unitPrice.trim()) {
      const n = Number(unitPrice);
      if (!Number.isFinite(n) || n < 0) {
        next.unitPrice = '0 이상의 숫자를 입력하세요.';
      }
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    createMu.mutate();
  }

  const masterPriceLabel = selectedMenu
    ? `마스터 단가: ${formatWon(selectedMenu.unitPrice)}`
    : '메뉴 선택 시 마스터 단가가 표시됩니다.';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="매장 메뉴 등록"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={createMu.isPending}>
            취소
          </Button>
          <Button
            variant="strong"
            onClick={submit}
            disabled={createMu.isPending || masterQ.isLoading}
          >
            {createMu.isPending ? '등록 중…' : '등록'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FormField
          label="회사 마스터 메뉴"
          required
          error={errors.menuId}
          hint="설정 > 레시피에서 추가한 메뉴 중 선택합니다."
        >
          {masterQ.isLoading ? (
            <div style={{ fontSize: 12, color: 'var(--fg-3)', padding: '8px 10px' }}>
              마스터 메뉴 불러오는 중…
            </div>
          ) : (
            <MasterMenuSearchInput
              menus={menus}
              selectedId={menuId}
              onSelect={(m) => {
                setMenuId(m.id);
                // unitPrice prefill — 마스터 단가
                if (!unitPrice) {
                  setUnitPrice(String(Math.round(Number(m.unitPrice ?? 0))));
                }
              }}
              excludeIds={existingMenuIds}
              error={!!errors.menuId}
            />
          )}
        </FormField>

        {selectedMenu && (
          <div
            style={{
              padding: '8px 12px',
              background: 'var(--bg-subtle)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 12,
              color: 'var(--fg-3)',
            }}
          >
            <div>
              <b style={{ color: 'var(--fg-1)' }}>{selectedMenu.name}</b>
              {selectedMenu.categoryName && (
                <span> · {selectedMenu.categoryName}</span>
              )}
            </div>
            <div style={{ marginTop: 2 }}>{masterPriceLabel}</div>
          </div>
        )}

        <FormField
          label="매장별 단가 override (원)"
          hint="비워두면 마스터 단가를 그대로 사용합니다."
          error={errors.unitPrice}
        >
          <TextInput
            type="number"
            value={unitPrice}
            onChange={setUnitPrice}
            placeholder={
              selectedMenu
                ? String(Math.round(Number(selectedMenu.unitPrice ?? 0)))
                : '9000'
            }
            error={!!errors.unitPrice}
          />
        </FormField>

        <FormField
          label="외부 메뉴 ID (선택)"
          hint="외부 판매채널의 메뉴 ID. 영수증 매칭에 사용됩니다."
        >
          <TextInput
            value={externalMenuId}
            onChange={setExternalMenuId}
            placeholder="예: 12345"
          />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── 매장 메뉴 편집 Modal (unitPrice override / externalMenuId / isActive) ───
function StoreMenuEditModal({
  open,
  onClose,
  storeId,
  target,
}: {
  open: boolean;
  onClose: () => void;
  storeId: string;
  target: StoreMenuRow | null;
}) {
  const toast = useToast();
  const [unitPrice, setUnitPrice] = useState('');
  const [externalMenuId, setExternalMenuId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<{ unitPrice?: string }>({});

  useEffect(() => {
    if (!open || !target) return;
    setUnitPrice(
      target.unitPrice != null
        ? String(Math.round(Number(target.unitPrice)))
        : '',
    );
    setExternalMenuId(target.externalMenuId ?? '');
    setIsActive(target.isActive);
    setErrors({});
  }, [open, target]);

  const updateMu = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error('편집 대상 없음');
      const body: Record<string, unknown> = {
        isActive,
      };
      // unitPrice: 빈 입력 → undefined (마스터 단가 유지). 기존이 override 인데 비우면
      // body 미전달 → 백엔드에서 변경 X. override 해제는 별도 endpoint 필요 (미스코프).
      if (unitPrice.trim()) {
        body.unitPrice = Number(unitPrice);
      }
      body.externalMenuId = externalMenuId.trim() || '';
      const r = await storeMenusControllerUpdate({
        path: { id: target.id },
        body: body as never,
      });
      return unwrap<{ item: StoreMenuRow }>(r);
    },
    onSuccess: async () => {
      toast.success('매장 메뉴를 저장했습니다.');
      await queryClient.invalidateQueries({
        queryKey: ['store-menus', { storeId }],
      });
      onClose();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '매장 메뉴 저장 실패');
    },
  });

  function submit() {
    const next: typeof errors = {};
    if (unitPrice.trim()) {
      const n = Number(unitPrice);
      if (!Number.isFinite(n) || n < 0) {
        next.unitPrice = '0 이상의 숫자를 입력하세요.';
      }
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    updateMu.mutate();
  }

  if (!target) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="매장 메뉴 편집"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updateMu.isPending}>
            취소
          </Button>
          <Button variant="strong" onClick={submit} disabled={updateMu.isPending}>
            {updateMu.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            padding: '8px 12px',
            background: 'var(--bg-subtle)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 12,
            color: 'var(--fg-3)',
          }}
        >
          <div>
            <b style={{ color: 'var(--fg-1)' }}>
              {target.menu?.name ?? '(메뉴 없음)'}
            </b>
            {target.menu?.categoryName && (
              <span> · {target.menu.categoryName}</span>
            )}
          </div>
          <div style={{ marginTop: 2 }}>
            마스터 단가: {formatWon(target.menu?.unitPrice ?? '0')}
          </div>
        </div>

        <FormField
          label="매장별 단가 override (원)"
          hint="비워두면 마스터 단가를 그대로 사용합니다."
          error={errors.unitPrice}
        >
          <TextInput
            type="number"
            value={unitPrice}
            onChange={setUnitPrice}
            placeholder="9000"
            error={!!errors.unitPrice}
          />
        </FormField>

        <FormField
          label="외부 메뉴 ID (선택)"
          hint="외부 판매채널의 메뉴 ID."
        >
          <TextInput
            value={externalMenuId}
            onChange={setExternalMenuId}
            placeholder="예: 12345"
          />
        </FormField>

        <FormField
          label="상태"
          hint="중지 시 영수증 매칭 / 분석에서 제외됩니다."
        >
          <Toggle
            checked={isActive}
            onChange={setIsActive}
            label={isActive ? '운영' : '중지'}
          />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── StoreInfoTab — store-info.tsx 의 핵심 로직 inline ────────────────
const STATUS_OPTIONS = [
  { value: 'active', label: '운영중' },
  { value: 'inactive', label: '점검중' },
];

function StoreInfoTab({
  store,
  platform,
  isOwner,
}: {
  store: StoreRow;
  platform: PlatformDto;
  isOwner: boolean;
}) {
  const toast = useToast();
  const [name, setName] = useState(store.name ?? '');
  const [brand, setBrand] = useState(store.brand ?? '');
  const [externalSiteId, setExternalSiteId] = useState(store.externalSiteId ?? '');
  const [status, setStatus] = useState<'active' | 'inactive'>(
    store.status === 'inactive' ? 'inactive' : 'active',
  );

  // store 가 바뀌면 폼 sync
  useEffect(() => {
    setName(store.name ?? '');
    setBrand(store.brand ?? '');
    setExternalSiteId(store.externalSiteId ?? '');
    setStatus(store.status === 'inactive' ? 'inactive' : 'active');
  }, [store.id, store.name, store.brand, store.externalSiteId, store.status]);

  const updateMu = useMutation({
    mutationFn: async () => {
      const r = await storesControllerUpdate({
        path: { id: store.id },
        body: {
          name: name.trim(),
          brand: brand.trim() || undefined,
          externalSiteId: externalSiteId.trim() || undefined,
          status,
        } as never,
      });
      return unwrap<{ item: StoreRow }>(r);
    },
    onSuccess: async () => {
      toast.success('매장 정보를 저장했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['stores', 'list'] });
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '저장 실패');
    },
  });

  const dirty =
    name !== (store.name ?? '') ||
    brand !== (store.brand ?? '') ||
    externalSiteId !== (store.externalSiteId ?? '') ||
    status !== (store.status === 'inactive' ? 'inactive' : 'active');
  const canSave = isOwner && !updateMu.isPending && name.trim().length > 0 && dirty;

  return (
    <div
      style={{
        padding: '16px 18px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        maxWidth: 560,
      }}
    >
      <FormField label="매장 이름" required>
        <TextInput value={name} onChange={setName} disabled={!isOwner} />
      </FormField>
      <FormField label="브랜드 (선택)">
        <TextInput value={brand} onChange={setBrand} disabled={!isOwner} />
      </FormField>
      <FormField label="외부 사이트 ID" hint="수신기가 보내는 site_id 와 매칭됩니다.">
        <TextInput
          value={externalSiteId}
          onChange={setExternalSiteId}
          disabled={!isOwner}
        />
      </FormField>
      <FormField label="상태">
        <Select
          value={status}
          onChange={(v) => setStatus(v as 'active' | 'inactive')}
          options={STATUS_OPTIONS}
          disabled={!isOwner}
        />
      </FormField>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          rowGap: 6,
          columnGap: 12,
          fontSize: 12,
          color: 'var(--fg-3)',
          padding: '12px 14px',
          background: 'var(--bg-subtle)',
          border: '1px solid var(--border-weak)',
          borderRadius: 'var(--radius-md)',
          marginTop: 6,
        }}
      >
        <div>판매채널</div>
        <div style={{ color: 'var(--fg-2)' }}>
          {platform.name} ({platform.id})
        </div>
        <div>매장 ID</div>
        <div style={{ color: 'var(--fg-2)', fontFamily: 'var(--font-mono)' }}>
          {store.id}
        </div>
        <div>BoxHero location</div>
        <div style={{ color: 'var(--fg-2)' }}>{store.boxheroLocationId ?? '—'}</div>
        <div>생성일</div>
        <div style={{ color: 'var(--fg-2)' }}>{store.createdAt}</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button
          variant="strong"
          disabled={!canSave}
          title={isOwner ? '' : '권한 없음 (owner 만)'}
          onClick={() => updateMu.mutate()}
        >
          {updateMu.isPending ? '저장 중…' : '저장'}
        </Button>
      </div>
    </div>
  );
}

function SectionHead({
  title,
  sub,
}: {
  title: string;
  sub?: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: 'var(--fg-1)',
          letterSpacing: '-0.01em',
        }}
      >
        {title}
      </div>
      {sub && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--fg-3)',
            marginTop: 4,
            lineHeight: 1.5,
            maxWidth: 560,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}
